import { useMutation } from '@tanstack/react-query';

import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type {
  StartConnectCrmImportArgs,
  StartConnectCrmImportResult,
} from '@/types/admin';

/**
 * Mutation wrapper around the `start_connect_crm_import` RPC.
 *
 * The RPC is super-admin gated server-side and returns a discriminated
 * jsonb response. The hook does NOT throw on idempotency conflict — those
 * surface as `data.conflict === true` so the UI can branch into the
 * Merge / Replace flow without an error toast. RPC errors (auth failure,
 * forbidden, validation) DO throw and are captured to GlitchTip.
 */
export function useStartConnectCrmImport() {
  return useMutation<StartConnectCrmImportResult, Error, StartConnectCrmImportArgs>({
    mutationFn: async (args) => {
      const { data, error } = await supabase.rpc('start_connect_crm_import', {
        p_user_id: args.user_id,
        p_workspace_id: args.workspace_id,
        p_connect_email: args.connect_email,
        p_password: args.password,
        p_idempotency: args.idempotency ?? 'block',
      });
      if (error) throw error;
      return data as StartConnectCrmImportResult;
    },
    onError: (err) => {
      captureError(err, { feature: 'connect-crm-import-start' });
    },
  });
}
